import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Input, Row, Select, Space, Table, Typography } from 'antd';
import { DownloadOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Breadcrumb from '@components/Breadcrumb';
import ContainerBody from '@components/ContainerBody';
import Page403 from '@page/Common/403/403';
import { showError } from '@components/notification';
import { usePermission } from '@contexts/Auth/UsePermission';
import {
  apiExportCustomerWaterConsumptions,
  apiGetCustomerWaterConsumptions
} from '@services/CustomerWaterConsumption/CustomerWaterConsumptionService';
import { formatCurrency, isNullOrEmpty } from '@utils/utils';

const currentYear = dayjs().year();
const initialFilters = {
  customer_code: '',
  month: undefined,
  year: currentYear
};

const CustomerWaterConsumption = () => {
  const { isSuperAdmin } = usePermission();
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiGetCustomerWaterConsumptions({
        page,
        size: pageSize,
        customer_code: appliedFilters.customer_code || undefined,
        month: appliedFilters.month,
        year: appliedFilters.year
      });
      const rows = response.data?.data || [];
      setData(
        rows.map((item, index) => ({
          ...item,
          key: item.id,
          stt: (page - 1) * pageSize + index + 1
        }))
      );
      setTotal(response.data?.meta?.total || 0);
    } catch (error) {
      setData([]);
      showError(error?.response?.data?.message || 'Không thể tải dữ liệu tiêu thụ CNHP.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const response = await apiExportCustomerWaterConsumptions({
        customer_code: appliedFilters.customer_code || undefined,
        month: appliedFilters.month,
        year: appliedFilters.year
      });
      const blob = new Blob([response.data], {
        type: response.headers['content-type']
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const period =
        appliedFilters.month && appliedFilters.year
          ? `_${String(appliedFilters.month).padStart(2, '0')}_${appliedFilters.year}`
          : '';
      link.href = url;
      link.setAttribute('download', `Du_lieu_tieu_thu_CNHP${period}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showError(error?.response?.data?.message || 'Không thể xuất dữ liệu tiêu thụ CNHP.');
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) fetchData();
  }, [isSuperAdmin, page, pageSize, appliedFilters]);

  if (!isSuperAdmin) {
    return (
      <ContainerBody>
        <Page403 />
      </ContainerBody>
    );
  }

  const numberCell = (value, precision = 0) =>
    isNullOrEmpty(value) ? '-' : formatCurrency(Number(value), precision);

  const columns = [
    { title: 'STT', dataIndex: 'stt', width: 70, fixed: 'left' },
    { title: 'Mã khách hàng', dataIndex: 'customer_code', width: 145, fixed: 'left' },
    { title: 'Tên khách hàng', dataIndex: 'customer_name', width: 220 },
    {
      title: 'Kỳ hóa đơn',
      width: 115,
      render: (_, record) =>
        `${String(record.billing_month).padStart(2, '0')}/${record.billing_year}`
    },
    {
      title: 'Từ ngày',
      dataIndex: 'from_date',
      width: 115,
      render: (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-')
    },
    {
      title: 'Đến ngày',
      dataIndex: 'to_date',
      width: 115,
      render: (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-')
    },
    {
      title: 'Chỉ số cũ',
      dataIndex: 'old_index',
      width: 110,
      render: (value) => numberCell(value, 3)
    },
    {
      title: 'Chỉ số mới',
      dataIndex: 'new_index',
      width: 110,
      render: (value) => numberCell(value, 3)
    },
    {
      title: 'Tiêu thụ (m3)',
      dataIndex: 'consumption_m3',
      width: 125,
      render: (value) => numberCell(value, 3)
    },
    { title: 'Tuyến', dataIndex: 'route_code', width: 100, render: (value) => value || '-' },
    {
      title: 'Tiền nước',
      children: [
        {
          title: 'Thành tiền',
          dataIndex: 'water_amount',
          width: 125,
          render: (value) => numberCell(value)
        },
        {
          title: 'VAT',
          dataIndex: 'water_vat',
          width: 105,
          render: (value) => numberCell(value)
        },
        {
          title: 'Tổng cộng',
          dataIndex: 'water_total',
          width: 125,
          render: (value) => numberCell(value)
        }
      ]
    },
    {
      title: 'Dịch vụ thoát nước',
      children: [
        {
          title: 'Thành tiền',
          dataIndex: 'drainage_amount',
          width: 125,
          render: (value) => numberCell(value)
        },
        {
          title: 'VAT',
          dataIndex: 'drainage_vat',
          width: 105,
          render: (value) => numberCell(value)
        },
        {
          title: 'Tổng cộng',
          dataIndex: 'drainage_total',
          width: 125,
          render: (value) => numberCell(value)
        }
      ]
    },
    {
      title: 'Tổng tiền',
      dataIndex: 'total_amount',
      width: 135,
      render: (value) => numberCell(value)
    },
    { title: 'Số tiền bằng chữ', dataIndex: 'amount_in_words', width: 300 },
    {
      title: 'Thời gian crawl',
      dataIndex: 'crawled_at',
      width: 165,
      render: (value) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-')
    }
  ];

  return (
    <ContainerBody>
      <div className="breadcrumb">
        <Breadcrumb
          homePath="/dashboard"
          routeSegments={[{ name: 'Dữ liệu tiêu thụ thực tế CNHP' }]}
        />
      </div>

      <Card>
        <Row justify="space-between" align="middle">
          <Col>
            <Typography.Title level={4}>Dữ liệu tiêu thụ thực tế CNHP</Typography.Title>
          </Col>
          <Col>
            <Button
              icon={<DownloadOutlined />}
              loading={exportLoading}
              style={{
                height: 36,
                color: '#fff',
                backgroundColor: '#0284c7',
                borderColor: '#0284c7'
              }}
              onClick={handleExport}>
              Xuất Excel
            </Button>
          </Col>
        </Row>

        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={10}>
            <Input
              value={filters.customer_code}
              placeholder="Mã khách hàng"
              allowClear
              style={{ height: 36 }}
              onChange={(event) => setFilters({ ...filters, customer_code: event.target.value })}
              onPressEnter={() => {
                setPage(1);
                setAppliedFilters(filters);
              }}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              value={filters.month}
              placeholder="Chọn tháng"
              allowClear
              style={{ width: '100%', height: 36 }}
              options={Array.from({ length: 12 }, (_, index) => ({
                value: index + 1,
                label: `Tháng ${index + 1}`
              }))}
              onChange={(month) => setFilters({ ...filters, month })}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              value={filters.year}
              placeholder="Chọn năm"
              allowClear
              style={{ width: '100%', height: 36 }}
              options={Array.from({ length: 7 }, (_, index) => ({
                value: currentYear - index,
                label: `Năm ${currentYear - index}`
              }))}
              onChange={(year) => setFilters({ ...filters, year })}
            />
          </Col>
          <Col xs={24} md={6}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                style={{ height: 36, backgroundColor: '#0284c7' }}
                onClick={() => {
                  setPage(1);
                  setAppliedFilters(filters);
                }}>
                Tìm kiếm
              </Button>
              <Button
                icon={<ReloadOutlined />}
                style={{ height: 36 }}
                onClick={() => {
                  setFilters(initialFilters);
                  setAppliedFilters(initialFilters);
                  setPage(1);
                }}>
                Đặt lại
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          bordered
          loading={loading}
          columns={columns}
          dataSource={data}
          scroll={{ x: 2400 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (value) => `Tổng ${value} bản ghi`,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPageSize !== pageSize ? 1 : nextPage);
              setPageSize(nextPageSize);
            }
          }}
        />
      </Card>
    </ContainerBody>
  );
};

export default CustomerWaterConsumption;
